import {
  ensureUniqueFormFieldIds,
  normalizePrefillEditFieldIds,
  resolvePrefillEditFieldId
} from '../../../../components/formBuilder/editForm/tableFieldUtils'

export interface PrefillPreviewEntry {
  fieldId: number
  label: string
  value: string
  editable: boolean
  editableOnCondition?: Record<string, unknown> | null
  editFieldValues?: Array<{
    fieldId: number
    label: string
    value: string
  }>
}

const NON_INPUT_FIELD_TYPES = new Set([
  'heading',
  'subheading',
  'banner',
  'formLogo',
  'divider',
  'button',
  'prevPageButton',
  'nextPageButton',
  'addPage',
  'subform',
  'buttonRow'
])

export const hasPrefillEditFields = (element: any): boolean =>
  Array.isArray(element?.prefillEditFields) && element.prefillEditFields.length > 0

export const isPrefillRecipientField = (element: any): boolean =>
  Boolean(element?.isPrefilled || element?.prefillLocked)

export const isConfiguredForConditionalPrefillEdit = (element: any): boolean => {
  if (element?.prefillEditable === false) return false
  if (!hasPrefillEditFields(element)) return false
  return element?.prefillEditMode !== 'always'
}

export const getConditionalPrefillActivationUpdates = (
  element: any
): { prefillConditionalEditActive?: boolean } => {
  if (!isConfiguredForConditionalPrefillEdit(element) || element?.prefillConditionalEditActive) {
    return {}
  }
  return { prefillConditionalEditActive: true }
}

export const isConditionalPrefillEditMode = (element: any): boolean =>
  isConfiguredForConditionalPrefillEdit(element) && isPrefillRecipientField(element)

const hasConditionalPrefillConfig = (element: any): boolean =>
  hasPrefillEditFields(element) && element.prefillEditMode !== 'always'

export const resolveRecipientCanEditMainField = (
  element: any,
  options?: { matchEditable?: boolean }
): boolean => {
  if (element?.prefillEditable === false) return false

  if (hasConditionalPrefillConfig(element)) return true

  const editMode = element?.prefillEditMode || 'always'
  if (editMode === 'always') {
    return element.prefillEditable !== false
  }

  const matchEditable = options?.matchEditable
  return matchEditable !== false && element.prefillEditable !== false
}

/** Recipient may edit the prefilled main field (allow edit and allow conditional edit). */
export const canEditPrefilledMainField = (element: any): boolean => {
  if (!isPrefillRecipientField(element)) return false
  return resolveRecipientCanEditMainField(element)
}

export const isDirectPrefillEditMode = canEditPrefilledMainField

export const isEditFieldValueEmpty = (editField: any): boolean => {
  if (editField?.type === 'switch') {
    return editField.value === undefined || editField.value === null || editField.value === ''
  }
  return !String(editField?.value ?? '').trim()
}

/** Legacy edit fields without the flag stay required so existing forms keep working. */
export const isPrefillEditFieldRequired = (
  editField: { isRequired?: boolean } | null | undefined
): boolean => editField?.isRequired !== false

export const getPrefillEditFieldRequiredMessage = (
  editField: { label?: string; requiredErrorMessage?: string } | null | undefined
): string =>
  String(editField?.requiredErrorMessage || '').trim() ||
  (editField?.label ? `${editField.label} is required` : 'This field is required')

export const validateConditionalPrefillEdits = (pages: any[][]) => {
  const errors: Record<string, string> = {}
  const fieldsToActivate: number[] = []

  pages.forEach((page) => {
    page.forEach((field) => {
      if (!isConditionalPrefillEditMode(field)) return
      if (!field.prefillConditionalEditActive) return

      const emptyRequiredFields: Array<{ editField: any; editFieldId: number }> = []
      ;(field.prefillEditFields || []).forEach((editField: any, index: number) => {
        if (!isPrefillEditFieldRequired(editField) || !isEditFieldValueEmpty(editField)) return
        emptyRequiredFields.push({
          editField,
          editFieldId: resolvePrefillEditFieldId(editField, index, field.fieldId)
        })
      })
      if (emptyRequiredFields.length === 0) return

      const fieldName =
        field.label && typeof field.label === 'string'
          ? field.label.toLowerCase()
          : `field_${field.fieldId}`
      errors[fieldName] =
        emptyRequiredFields.length === 1
          ? getPrefillEditFieldRequiredMessage(emptyRequiredFields[0].editField)
          : 'Please complete all required edit fields'
      emptyRequiredFields.forEach(({ editField, editFieldId }) => {
        errors[`edit_${field.fieldId}_${editFieldId}`] =
          getPrefillEditFieldRequiredMessage(editField)
      })
      fieldsToActivate.push(field.fieldId)
    })
  })

  return {
    errors,
    fieldsToActivate,
    hasErrors: fieldsToActivate.length > 0
  }
}

const getSampleValueForField = (element: any): string => {
  if (element.value != null && String(element.value).trim()) {
    return String(element.value)
  }

  switch (element.type) {
    case 'email':
      return 'preview@example.com'
    case 'tel':
      return '+1 555 0100'
    case 'url':
      return 'https://example.com'
    case 'number':
      return '42'
    case 'date':
      return new Date().toISOString().slice(0, 10)
    case 'time':
      return '09:00'
    case 'datetime':
      return new Date().toISOString().slice(0, 16)
    case 'switch':
      return 'true'
    case 'ratings':
      return '4'
    case 'select':
    case 'radio':
      return element.options?.[0]?.text || element.options?.[0]?.value || 'Option 1'
    case 'checkbox':
      return element.options?.[0]?.text ? `${element.options[0].text};` : 'Option 1;'
    case 'table':
      return element.value || ''
    default:
      return element.label ? `Sample ${element.label}` : 'Sample value'
  }
}

export const buildSamplePrefillEntries = (pages: any[][]): PrefillPreviewEntry[] => {
  const entries: PrefillPreviewEntry[] = []

  pages.flat().forEach((element) => {
    if (!element?.label || NON_INPUT_FIELD_TYPES.has(element.type)) return

    const sampleValue = getSampleValueForField(element)
    if (!sampleValue && element.type !== 'switch') return

    entries.push({
      fieldId: element.fieldId,
      label: element.label,
      value: sampleValue,
      editable: element.prefillEditable !== false
    })
  })

  return entries
}

export const preparePreviewPages = (
  formData: any,
  options?: { prefillEntries?: PrefillPreviewEntry[]; isPrefillForm?: boolean }
): any[][] => {
  const pages = ensureUniqueFormFieldIds(
    normalizeRecipientPrefillPages(normalizeFormPages(formData))
  )
  const withSettings = pages.map((page) =>
    (page || []).map((element: any) => ({
      ...element,
      prefillEditable:
        element.prefillEditable !== undefined ? element.prefillEditable : element.type !== 'email'
    }))
  )

  if (options?.prefillEntries?.length) {
    return applyPrefillToFormData(withSettings, options.prefillEntries)
  }

  if (options?.isPrefillForm) {
    return applyPrefillToFormData(withSettings, buildSamplePrefillEntries(withSettings))
  }

  return withSettings
}

const normalizeFormPages = (formData: any): any[][] => {
  if (!Array.isArray(formData) || formData.length === 0) return [[]]
  if (Array.isArray(formData[0])) return formData
  return [formData]
}

const normalizeRecipientPrefillElement = (element: any): any => {
  if (!element || typeof element !== 'object') return element

  const editFields = Array.isArray(element.prefillEditFields) ? element.prefillEditFields : []
  const hasConditionalFields = editFields.length > 0

  let prefillEditMode = element.prefillEditMode
  if (
    hasConditionalFields &&
    prefillEditMode !== 'editFields' &&
    prefillEditMode !== 'conditional'
  ) {
    prefillEditMode = 'editFields'
  }

  return {
    ...element,
    prefillEditable:
      element.prefillEditable !== undefined ? element.prefillEditable : element.type !== 'email',
    prefillEditMode: prefillEditMode || 'always',
    prefillEditFields: normalizePrefillEditFieldIds(
      editFields,
      element.type === 'table' ? undefined : Number(element.fieldId ?? element.id)
    )
  }
}

const normalizeRecipientPrefillPages = (pages: any[][]): any[][] =>
  pages.map((page) => (page || []).map((element) => normalizeRecipientPrefillElement(element)))

const parseMultiValue = (value: string): string[] =>
  value
    .split(/[,;|]/)
    .map((v) => v.trim())
    .filter(Boolean)

const normalizeRatingsPrefillValue = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return trimmed
  if (/^[1-5]$/.test(trimmed)) return trimmed

  const numeric = Number(trimmed)
  if (Number.isFinite(numeric) && numeric >= 1 && numeric <= 5) {
    return String(Math.round(numeric))
  }

  const excelRatingDate = trimmed.match(/^1900-01-0?([1-5])(?:T|\s|$)/)
  if (excelRatingDate) return excelRatingDate[1]

  const parsed = new Date(trimmed)
  if (!Number.isNaN(parsed.getTime()) && parsed.getFullYear() === 1900 && parsed.getMonth() === 0) {
    const day = parsed.getDate()
    if (day >= 1 && day <= 5) return String(day)
  }

  return trimmed
}

const getEditFieldValue = (
  editField: any,
  editFieldValues?: PrefillPreviewEntry['editFieldValues']
) => {
  const match = editFieldValues?.find((entry) => entry.fieldId === editField.fieldId)
  return match?.value ?? editField.value ?? ''
}

const applyPrefillToElement = (element: any, match: PrefillPreviewEntry) => {
  const editFields = Array.isArray(element.prefillEditFields)
    ? element.prefillEditFields.map((editField: any) => ({
        ...editField,
        value: getEditFieldValue(editField, match.editFieldValues)
      }))
    : []

  const recipientCanEdit = resolveRecipientCanEditMainField(element, {
    matchEditable: match.editable
  })

  const base = {
    ...element,
    value: match.value ?? '',
    prefillLocked: true,
    isPrefilled: true,
    prefillConditionalEditActive: false,
    prefillMainFieldEditable: recipientCanEdit,
    prefillEditableOnCondition:
      match.editableOnCondition ?? element.prefillEditableOnCondition ?? null,
    prefillEditFields: editFields
  }

  if (element.type === 'checkbox' && Array.isArray(element.options)) {
    const selected = parseMultiValue(match.value)
    return {
      ...base,
      options: element.options.map((option: any) => ({
        ...option,
        selected:
          selected.includes(option.text) ||
          selected.includes(option.value) ||
          selected.includes(String(option.text))
      }))
    }
  }

  if (element.type === 'radio' && Array.isArray(element.options)) {
    return {
      ...base,
      options: element.options.map((option: any) => ({
        ...option,
        selected: match.value === option.text || match.value === option.value
      }))
    }
  }

  if (element.type === 'select' && Array.isArray(element.options)) {
    return {
      ...base,
      options: element.options.map((option: any) => ({
        ...option,
        selected: match.value === option.text || match.value === option.value
      }))
    }
  }

  if (element.type === 'switch') {
    const truthy = ['true', '1', 'yes', 'on'].includes(match.value.toLowerCase())
    return { ...base, checked: truthy, value: truthy }
  }

  if (element.type === 'ratings') {
    const ratingValue = normalizeRatingsPrefillValue(String(match.value || ''))
    const numericRating = Number(ratingValue)
    return {
      ...base,
      value: numericRating >= 1 && numericRating <= 5 ? ratingValue : ''
    }
  }

  return base
}

export const applyPrefillToFormData = (
  formData: any,
  prefillEntries: PrefillPreviewEntry[]
): any[][] => {
  const pages = normalizeRecipientPrefillPages(normalizeFormPages(formData))

  return pages.map((page) =>
    (page || []).map((element: any) => {
      const elementFieldId = Number(element?.fieldId ?? element?.id)
      const match = prefillEntries.find((p) => {
        const prefillFieldId = Number(p.fieldId)
        if (
          Number.isFinite(prefillFieldId) &&
          Number.isFinite(elementFieldId) &&
          prefillFieldId === elementFieldId
        ) {
          return true
        }
        return Boolean(p.label && element.label && p.label === element.label)
      })
      if (!match) return element
      return applyPrefillToElement(element, match)
    })
  )
}

export const resolveShareableLinkForPreview = (shareableLink: string): string => {
  const localBase = process.env.REACT_APP_PUBLIC_FORM_BASE_URL
  if (!localBase || !shareableLink) return shareableLink

  try {
    const parsed = new URL(shareableLink)
    const local = new URL(localBase)
    parsed.protocol = local.protocol
    parsed.host = local.host
    return parsed.toString()
  } catch {
    return shareableLink
  }
}
